import { useState } from 'react';
import { GitCompare, X } from 'lucide-react';
import {
  useCheckpoints,
  useRewindToCheckpoint,
  type Checkpoint,
} from '@/services/checkpointService';
import { useConversation } from '@/services/conversationService';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { CheckpointDiffModal } from '@/components/chat/CheckpointDiffModal';

interface CheckpointTimelineProps {
  conversationId: string | undefined;
}

export function CheckpointTimeline({ conversationId }: CheckpointTimelineProps) {
  const { t } = useTranslation('common');
  const { data: checkpoints = [], isLoading } = useCheckpoints(conversationId);
  const { conversation } = useConversation();
  const rewind = useRewindToCheckpoint(conversationId);
  // Index of the checkpoint the user picked to compare against (or null when
  // not in compare mode). When set, the next click on a different card opens
  // the diff modal between the picked one and the second one.
  const [compareBaseId, setCompareBaseId] = useState<string | null>(null);
  const [diffPair, setDiffPair] = useState<
    { base: Checkpoint; head: Checkpoint; baseIdx: number; headIdx: number } | null
  >(null);

  if (!conversationId) return null;
  if (isLoading) return null;
  if (checkpoints.length === 0) return null;

  const currentId = conversation.currentCheckpointId ?? null;

  const handleSelect = (cp: Checkpoint, idx: number) => {
    if (compareBaseId === null) {
      // Not in compare mode — single-click rewinds.
      rewind.mutate(cp.id);
      return;
    }
    if (compareBaseId === cp.id) {
      setCompareBaseId(null);
      return;
    }
    const base = checkpoints.find((c) => c.id === compareBaseId);
    const baseIdx = checkpoints.findIndex((c) => c.id === compareBaseId);
    if (!base) {
      setCompareBaseId(null);
      return;
    }
    setDiffPair({ base, head: cp, baseIdx: baseIdx + 1, headIdx: idx + 1 });
    setCompareBaseId(null);
  };

  return (
    <>
      <div className="flex items-center gap-2 overflow-x-auto px-2 py-2 border-b bg-muted/40">
        <button
          type="button"
          onClick={() =>
            setCompareBaseId((id) => (id === null ? checkpoints[checkpoints.length - 1].id : null))
          }
          title={t('checkpointCompareTooltip', {
            defaultValue: 'Compare two checkpoints — pick a base, then a target.',
          })}
          className={cn(
            'shrink-0 flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition',
            compareBaseId !== null
              ? 'border-brand-indigo bg-brand-teal text-brand-indigo'
              : 'border-k-line text-brand-indigo hover:border-brand-indigo hover:bg-brand-teal-soft/40',
          )}
        >
          {compareBaseId !== null ? <X className="h-4 w-4" /> : <GitCompare className="h-4 w-4" />}
        </button>
        {checkpoints.map((c, idx) => (
          <CheckpointCard
            key={c.id}
            checkpoint={c}
            index={idx + 1}
            isCurrent={c.id === currentId}
            isCompareBase={c.id === compareBaseId}
            compareMode={compareBaseId !== null}
            onClick={() => handleSelect(c, idx)}
          />
        ))}
      </div>
      {diffPair && (
        <CheckpointDiffModal
          base={diffPair.base}
          head={diffPair.head}
          baseLabel={`#${diffPair.baseIdx}`}
          headLabel={`#${diffPair.headIdx}`}
          onClose={() => setDiffPair(null)}
        />
      )}
    </>
  );
}

function CheckpointCard({
  checkpoint,
  index,
  isCurrent,
  isCompareBase,
  compareMode,
  onClick,
}: {
  checkpoint: Checkpoint;
  index: number;
  isCurrent: boolean;
  isCompareBase: boolean;
  compareMode: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('common');
  const parts = checkpoint.spec?.parts?.length ?? 0;
  const joints = checkpoint.spec?.joints?.length ?? 0;
  const features = checkpoint.spec?.features?.length ?? 0;
  const params = checkpoint.spec?.parameters?.length ?? 0;
  const hashShort = checkpoint.specHash?.slice(0, 7) ?? '';
  const title = `#${index} ${checkpoint.toolCallSummary}`;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!compareMode && isCurrent}
      title={
        compareMode
          ? `Compare ${title}`
          : `${title}\n${parts} ${t('parts')}, ${joints} ${t('joints')}, ${features} features, ${params} params${hashShort ? `\n#${hashShort}` : ''}\n${new Date(checkpoint.createdAt).toLocaleString()}`
      }
      className={cn(
        'shrink-0 w-24 h-24 rounded-lg border-2 text-xs flex flex-col items-center justify-center transition-colors',
        isCompareBase
          ? 'border-brand-teal bg-brand-teal/15 cursor-pointer'
          : isCurrent && !compareMode
            ? 'border-brand-indigo bg-brand-teal-soft/40 cursor-default'
            : 'border-k-line hover:border-brand-indigo hover:bg-brand-teal-soft/30 cursor-pointer',
      )}
    >
      {checkpoint.thumbnailUrl ? (
        <img
          src={checkpoint.thumbnailUrl}
          alt={title}
          className="w-full h-full object-cover rounded-md"
        />
      ) : (
        <>
          <span className="font-semibold">#{index}</span>
          <span className="text-[10px] text-muted-foreground">
            {parts}p / {joints}j
          </span>
          {(features > 0 || params > 0) && (
            <span className="text-[9px] text-muted-foreground/80">
              {features}f · {params} param
            </span>
          )}
          {hashShort && (
            <span className="mt-0.5 rounded bg-brand-indigo px-1 font-mono text-[8px] tracking-wider text-brand-cream">
              {hashShort}
            </span>
          )}
        </>
      )}
    </button>
  );
}
